/* eslint-disable no-unused-vars */
import React, { useEffect, useContext, useState, useMemo } from 'react';
import { useParams } from 'react-router';
import { useQuery } from 'react-fetching-library';
import { PATIENT_TABLE_NAMES, ALL_ROUTES } from 'config/routes';
import RemoveDialog from 'components/Dialogs/RemoveDialog';
import useFetchList from 'hooks/useFetchList';
import { AppRouteContext } from 'contexts';
import useMetadataUpdate from 'hooks/useMetadataUpdate';
import showServerError from 'helpers/showError';
import useOrderMetadata from 'hooks/useOrderMetadata';
import PatientCreateModal from '../Item/Create/PatientCreateModal';
import PatientEditModal from '../Item/Edit/PatientEditModal';
import { useDispatch, useSelector } from 'react-redux';
import { NULL_QUERY_DELAY } from '../../Shared/constants';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
} from '../../../redux_store/reducer/reducers/notificationsReducer';
import { getTransitionWithVariable } from '../../../redux_store/reducer/reducers/i18nReducer';
import QhsTable from '../../Shared/Table/Table';
import { convertDataFromApi, useGenerateColumns } from '../../Shared/Table/helpers';
import TableTitle from '../../Shared/Table/widgets/TableTitle';
import AddButton from '../../Shared/Table/widgets/AddButton';
import TablePageContainer from '../../Shared/Table/widgets/TablePageContainer';
import { PATIENTS_COLUMNS } from '../constants';
import RowActions from './widgets/RowActions';
import { TABLE_KEYS } from '../../../redux_store/reducer/reducers/tableReducer/tableReducer';


// {true && (
//   <Button
//     color='primary'
//     variant={activeItemNumber === 1 ? 'contained' : 'outlined'}
//     className={cx(classes.resourceButton, {
//       [classes.activeButton]: activeItemNumber === 1,
//     })}
//     onClick={() => changeTable(routes.patientProgramList.path)}
//   >
//     {btn.programs}
//   </Button>
// )}


/*
  could be rendered in 2 cases:
  - patients for a current account
  - patients for a special account (accountUuid in URL params)
*/
const PatientsList = () => {
  const {
    en: {
      titles,
      notifications,
      errors,
      btn,
      dialogs,
    },
  } = useSelector((state) => state.i18n);
  const dispatch = useDispatch();
  const { accountUuid } = useParams();
  const {
    user,
    metadata: {
      currentAccount,
    },
    isDistributionSponsor,
  } = useSelector((state) => state.user);
  const [isOpenRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [openPatientCreateModal, togglePatientCreateModal] = useState(false);
  const [openPatientEditModal, togglePatientEditModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState({});
  const [removingPatient, setRemovingPatient] = useState({});
  const [orderPatient, setOrderPatient] = useState({});
  const { setAppRoute, breadcrumbs } = useContext(AppRouteContext);
  const tableKey = isDistributionSponsor ? TABLE_KEYS.patientsDSTable : TABLE_KEYS.patientsTable;
  const columns = useGenerateColumns(PATIENTS_COLUMNS, tableKey, {}, {}).columns;
  const fields = columns.map((columnData) => columnData.accessorKey).join();

  const { loading: metadataLoading } = useOrderMetadata(
    orderPatient.uuid,
    orderPatient.accountUUID,
    () => {
      showServerError(dispatch, errors.noProvidersOnThisAccount);
      setOrderPatient({});
    },
    {
      actionTitle: titles.editPatient,
      actionCallback: () => {
        togglePatientEditModal(true);
      },
      callback: () => {
        setOrderPatient({});
      },
    },
  );

  const {
    items,
    totalCount,
    reloadData,
    clearFilter,
    onLoadMoreRows,
    sort,
    onSort,
    filter,
    onFilter,
    loading: dataLoading,
  } = useFetchList({
    baseEndpoint: `/accounts/${accountUuid || currentAccount.uuid}/patients`,
    baseParams: { fields: fields },
    tableKey,
    canLoad: fields.length,
  });

  const { query: queryRemove, loading: loadingRemove } = useMetadataUpdate(
    useQuery,
    [
      {
        method: 'DELETE',
        endpoint: `/accounts/${removingPatient.accountUUID}/patients/${removingPatient.uuid}`,
      },
      false,
    ],
  );

  const [prevTotalCount, setPrevTotalCount] = useState(totalCount);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  useEffect(() => {
    if (prevTotalCount !== totalCount) {
      setPrevTotalCount(totalCount);
    }
  }, [filter, totalCount]);

  const handleRowClick = (props) => {
    const { uuid, accountUUID, firstName, lastName } = props;
    const isItChildAccount = accountUUID !== currentAccount.uuid && isDistributionSponsor;
    setAppRoute({
      path: (isItChildAccount ? ALL_ROUTES.patient.dashboardDS : ALL_ROUTES.patient.dashboard).path,
      params: {
        currentAccountUuid: currentAccount.uuid,
        accountUuid: isItChildAccount ? accountUUID : null,
        patientUuid: uuid,
        tableName: PATIENT_TABLE_NAMES.assessment,
      },
      title: `${titles.patientDashboard}: ${firstName} ${lastName}`,
    });
  };

  const handleCloseRemoveDialog = () => {
    setOpenRemoveDialog(false);
    setRemovingPatient({});
  };

  const handleRemove = () => {
    const { uuid, firstName, lastName } = removingPatient;
    if (uuid) {
      queryRemove().then(({ error, payload, status }) => {
        const name = `${firstName} ${lastName}`;
        if (!error) {
          setIsSuccess(true);
          setTimeout(() => {
            setIsSuccess(false);
          }, 2000);
          reloadData();
          dispatch(
            enqueueSnackbar({
              message: notifications.patientRemoved,
              options: {
                variant: SNACKBAR_VARIANTS.success,
              },
            }),
          );
        } else {
          setIsFailed(true);
          setTimeout(() => {
            setIsFailed(false);
          }, 2000);
          const options = payload && {
            correlationUUID: payload.correlationUUID,
            userUUID: user.uuid,
          };
          switch (status) {
            case 404: {
              showServerError(dispatch, errors.patientCouldNotFind);
              break;
            }
            case 500: {
              switch (payload.errorCode) {
                case 1: {
                  showServerError(
                    dispatch,
                    getTransitionWithVariable(errors.patientCantBeRemoved, {
                      name: name,
                    }),
                  );
                  break;
                }
                default: {
                  showServerError(dispatch, '', options);
                  break;
                }
              }
              break;
            }
            default: {
              showServerError(dispatch, '', options);
              break;
            }
          }
        }
        setOpenRemoveDialog(false);
        setRemovingPatient({});
      });
    }
  };

  const patients = useMemo(
    () => items ? convertDataFromApi(items, tableKey, filter) : null,
    [items],
  );

  const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
  const titleText = lastBreadcrumb
    ? lastBreadcrumb.title
    : titles.patients;

  const reloadPatientsList = (hasFilter) => {
    if (!hasFilter) {
      clearFilter();
    } else {
      reloadData({ delay: NULL_QUERY_DELAY });
    }
  };

  const hasFilters = useMemo(
    () => !!filter?.reduce((accumulator, { value }) => {
      return accumulator + value.toString().replaceAll(' ', '').length;
    }, 0),
    [filter],
  );

  return <>
    <PatientCreateModal
      reloadData={reloadData}
      open={openPatientCreateModal}
      accountUuid={accountUuid || currentAccount.uuid}
      handleClose={() => togglePatientCreateModal(false)}
    />
    {!!selectedPatient.patientUuid && (
      <PatientEditModal
        open={openPatientEditModal}
        patientUuid={selectedPatient.patientUuid}
        accountUuid={selectedPatient.accountUuid}
        handleClose={() => togglePatientEditModal(false)}
      />
    )}
    <TablePageContainer>
      <QhsTable
        columns={columns}
        data={patients || []}
        fetchNextPage={onLoadMoreRows}
        isFetching={dataLoading}
        isLoading={dataLoading && !patients}
        totalRowCount={totalCount}
        sorting={sort}
        setSorting={onSort}
        filter={filter}
        setFilters={onFilter}
        tableKey={tableKey}
        onRowClick={handleRowClick}
        showRefreshTableButton
        reloadData={reloadPatientsList}
        menuActions={isDistributionSponsor && !accountUuid
          ? undefined
          : <AddButton label={btn.patient} onClick={() => togglePatientCreateModal(true)} />
        }
        tableTitle={<TableTitle
          titleText={titleText}
          count={totalCount}
          countFiltered={hasFilters}
        />}
        renderRowActionMenuItems={({ row, setAnchorEl }) => (
          <RowActions
            row={row.original}
            setAnchorEl={setAnchorEl}
            setSelectedPatient={setSelectedPatient}
            setOrderPatient={setOrderPatient}
            togglePatientEditModal={togglePatientEditModal}
            setOpenRemoveDialog={setOpenRemoveDialog}
            setRemovingPatient={setRemovingPatient}
          />
        )}
      />
      <RemoveDialog
        open={isOpenRemoveDialog}
        onClose={handleCloseRemoveDialog}
        onSubmit={handleRemove}
        title={getTransitionWithVariable(dialogs.confirmRemove, {
          name: titles.patient,
        })}
        isLoading={loadingRemove}
        isSuccess={isSuccess}
        isFailed={isFailed}
      />
    </TablePageContainer>
  </>;
};

export default PatientsList;
